<script setup>
import { computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { matchesSearchTerm } from 'shared/helpers/textSearch';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import TruncatedText from './TruncatedText.vue';
import DropdownContainer from 'next/dropdown-menu/base/DropdownContainer.vue';
import DropdownSection from 'next/dropdown-menu/base/DropdownSection.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import DropdownItem from 'next/dropdown-menu/base/DropdownItem.vue';

const { options, maxChips } = defineProps({
  options: {
    type: Array,
    required: true,
  },
  maxChips: {
    type: Number,
    default: 3,
  },
});

const { t } = useI18n();
const selected = defineModel({
  type: [Array, String],
  required: true,
});

const hasItems = computed(() => {
  if (!selected.value) return false;
  if (!Array.isArray(selected.value)) return false;
  if (selected.value.length === 0) return false;

  return true;
});

const selectedIds = computed(() => {
  if (!hasItems.value) return [];
  return selected.value.map(value => value.id);
});

const selectedItems = computed(() => {
  // Options has additional properties, so we need to use them directly
  if (!hasItems.value) return [];
  return options.filter(option => selectedIds.value.includes(option.id));
});

const selectedVisibleItems = computed(() => {
  if (!hasItems.value) return [];
  // avoid showing "+1 more" coz it takes up space anway, might as well show it
  if (selectedItems.value.length === maxChips + 1) return selectedItems.value;
  // if we have more than maxChips then show only maxChips
  return selectedItems.value.slice(0, maxChips);
});

const remainingItems = computed(() => {
  if (!hasItems.value) return [];
  if (selectedItems.value.length === maxChips + 1) return [];
  return selectedItems.value.slice(maxChips);
});

const remainingTooltip = computed(() => {
  if (!hasItems.value) return '';
  return remainingItems.value.map(item => item.name).join(', ');
});

// The already selected options are pulled to the top of the list, but the order
// is only refreshed when the dropdown is opened. Reordering on every click would
// make an option jump away from under the cursor while picking.
const pinnedIds = ref([]);

const byName = (a, b) => (a.name || '').localeCompare(b.name || '');

// Options are grouped by the icon they show — the inboxes keep all e-mails
// together, all Gmail inboxes together, all phones together, … — and every group
// is alphabetical. Lists whose options have no icon (statuses, priorities) or a
// per-option one (the label dots) keep the deliberate order they were built with.
const groupKey = option => (typeof option.icon === 'string' ? option.icon : '');

const isGrouped = computed(() => options.some(option => groupKey(option)));

// Plain comparison, not localeCompare: an icon name is not prose, and collation
// rules for the digits and hyphens in it put `i-ri-chat-1-fill` after
// `i-ri-google-fill`, which scatters the groups.
const byGroup = (a, b) => {
  const [keyA, keyB] = [groupKey(a), groupKey(b)];
  if (keyA === keyB) return 0;
  return keyA < keyB ? -1 : 1;
};

const sortedOptions = computed(() => {
  if (!isGrouped.value) return options;

  return [...options].sort((a, b) => byGroup(a, b) || byName(a, b));
});

const orderedOptions = computed(() => {
  if (!pinnedIds.value.length) return sortedOptions.value;

  const pinned = [];
  const rest = [];
  sortedOptions.value.forEach(option => {
    if (pinnedIds.value.includes(option.id)) pinned.push(option);
    else rest.push(option);
  });

  pinned.sort(byName);

  return [...pinned, ...rest];
});

// Short fixed lists (statuses, priorities, CSAT) fit without scrolling; the
// search box only shows on the longer ones (labels, agents, inboxes, teams).
const SEARCH_MIN_OPTIONS = 8;
const showSearch = computed(() => options.length >= SEARCH_MIN_OPTIONS);

const searchTerm = ref('');
const searchInput = ref(null);

// Filters after the ordering above, so the selected options stay pinned on top.
const visibleOptions = computed(() => {
  if (!showSearch.value) return orderedOptions.value;

  return orderedOptions.value.filter(option =>
    matchesSearchTerm(option.name, searchTerm.value)
  );
});

const openDropdown = toggle => {
  pinnedIds.value = [...selectedIds.value];
  searchTerm.value = '';
  toggle();
  // `autofocus` does not fire for an input inserted after the page loaded.
  nextTick(() => searchInput.value?.focus());
};

const toggleOption = option => {
  // Ensure that the `icon` prop is not included, icon is a VNode which has circular references
  // This causes an error when creating a clone using JSON.parse(JSON.stringify())
  const optionToToggle = {
    id: option.id,
    name: option.name,
  };

  const idToToggle = optionToToggle.id;

  if (!hasItems.value) {
    selected.value = [optionToToggle];
    return;
  }

  if (selectedIds.value.includes(idToToggle)) {
    selected.value = selected.value.filter(value => value.id !== idToToggle);
  } else {
    selected.value = [...selected.value, optionToToggle];
  }
};
</script>

<template>
  <DropdownContainer>
    <template #trigger="{ toggle }">
      <button
        v-if="hasItems"
        class="bg-n-alpha-2 py-2 rounded-lg h-8 flex items-center px-0"
        @click="openDropdown(toggle)"
      >
        <TruncatedText
          v-for="item in selectedVisibleItems"
          :key="item.name"
          :label="item.name"
          class="px-3 border-r rtl:border-l rtl:border-r-0 border-n-weak text-n-slate-12 text-sm flex gap-2 items-center max-w-[100px]"
        >
          <template #icon>
            <Icon v-if="item.icon" :icon="item.icon" class="flex-shrink-0" />
          </template>
        </TruncatedText>
        <div
          v-if="remainingItems.length > 0"
          v-tooltip.top="remainingTooltip"
          class="px-3 border-r rtl:border-l rtl:border-r-0 border-n-weak text-n-slate-12 text-sm flex gap-2 items-center max-w-[100px]"
        >
          <span class="truncate">{{
            t('COMBOBOX.MORE', { count: remainingItems.length })
          }}</span>
        </div>
        <div class="flex items-center border-none px-3 gap-2">
          <Icon icon="i-lucide-plus" />
        </div>
      </button>
      <Button v-else sm slate faded @click="openDropdown(toggle)">
        <template #icon>
          <Icon icon="i-lucide-plus" class="text-n-slate-11" />
        </template>
        <span class="text-n-slate-11">{{ t('COMBOBOX.PLACEHOLDER') }}</span>
      </Button>
    </template>
    <DropdownBody class="top-0 min-w-48 z-50" strong>
      <div v-if="showSearch" class="relative">
        <Icon class="absolute size-4 left-2 top-2" icon="i-lucide-search" />
        <input
          ref="searchInput"
          v-model="searchTerm"
          class="p-1.5 pl-8 text-n-slate-11 bg-n-alpha-1 rounded-lg w-full"
          :placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        />
      </div>
      <DropdownSection class="max-h-80 overflow-scroll">
        <DropdownItem
          v-for="option in visibleOptions"
          :key="option.id"
          :icon="option.icon"
          preserve-open
          @click="toggleOption(option)"
        >
          <template #label>
            {{ option.name }}
            <Icon
              v-if="selectedIds.includes(option.id)"
              icon="i-lucide-check"
              class="bg-n-blue-text pointer-events-none"
            />
          </template>
        </DropdownItem>
        <DropdownItem
          v-if="searchTerm && !visibleOptions.length"
          disabled
          preserve-open
        >
          {{ t('COMBOBOX.EMPTY_SEARCH_RESULTS', { searchTerm }) }}
        </DropdownItem>
      </DropdownSection>
    </DropdownBody>
  </DropdownContainer>
</template>
